<template>
    <div class="results w-full">
        <div ref="loadingContainer">
            <wine-loading :start="loadStart"></wine-loading>
        </div>

        <div ref="resultContainer" class="h-0 overflow-hidden">
            <div class="results__content flex flex-col items-center">
                <h2 v-if="translations" ref="heading" class="text-center text-white opacity-0">
                    {{ products.length > 1 ? heading : translations.results.headingSingular }}
                </h2>
                <div class="results__carousel-container w-[256px] md:w-full">
                    <div
                        v-if="products && products.length > 0"
                        ref="container"
                        :class="{
                            'md:justify-center': products.length < 3,
                        }"
                        class="results__product-container keen-slider mt-4"
                    >
                        <div
                            v-for="(product, index) in products"
                            ref="slides"
                            :key="`product-${index}`"
                            :class="[
                                `number-slide${index}`,
                                {
                                    'opacity-50 md:opacity-100': index !== currentProductIndex,
                                    'results__product--1': products.length === 1,
                                    'results__product--2': products.length === 2,
                                },
                            ]"
                            class="results__product product min-w-[256px] max-w-[256px] md:min-w-0 md:max-w-full keen-slider__slide"
                        >
                            <product-result-card :product="product" :products-count="products.length" :index="index" :translations="translations"></product-result-card>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import gsap from 'gsap';
    import 'keen-slider/keen-slider.min.css';
    import KeenSlider from 'keen-slider';
    import WineLoading from '@/app/components/WineLoading.vue';
    import ProductResultCard from '@/app/components/ProductResultCard.vue';

    export default {
        name: 'MatchmakerResults',

        props: {
            products: { type: Array, default: () => [] },
            translations: { type: Object, default: () => {} },
        },

        components: { WineLoading, ProductResultCard },

        data: () => {
            return {
                loadStart: false,
                currentProductIndex: 0,
            };
        },

        computed: {
            heading() {
                return this.translations.results.heading.replace('{n}', this.products.length);
            },
        },

        watch: {
            currentProductIndex() {
                this.$emit('update-current-product-index', this.currentProductIndex);
            },
        },

        mounted() {
            // Tracking
            window.dataLayer.push({
                event: 'assistant_sommelier_etape',
                numero_etape: 4,
                nom_etape: 'page_resultats',
                choix: null,
            });

            // Wait half a second before showing the loading screen
            setTimeout(() => {
                this.loadStart = true;
            }, 500);

            // Wait a random amount of time before showing the results
            setTimeout(
                () => {
                    // Show restart button
                    gsap.to(document.querySelector('.restart-button'), {
                        opacity: 1,
                        display: 'block',
                        duration: 0.4,
                    });

                    this.switchToResult();

                    if (this.products.length > 1) {
                        window.dataLayer.push({
                            event: 'view_item_list',
                            ecommerce: {
                                item_list_id: 'recommandation_sommelier',
                                item_list_name: 'Suggestions du Sommelier',
                                items: this.products.map((product, index) => {
                                    return {
                                        item_id: product.sku ?? product.id,
                                        item_name: product.name,
                                        item_category: `Vin ${product.color.value}`,
                                        item_variant: product.degree ?? '',
                                        location_id: product.region ?? '',
                                        index: index + 1,
                                    };
                                }),
                            },
                        });
                    } else {
                        window.dataLayer.push({
                            event: 'view_item',
                            ecommerce: {
                                currency: 'EUR',
                                value: 0,
                                items: this.products.map((product) => {
                                    return {
                                        item_id: product.sku ?? product.id,
                                        item_name: product.name,
                                        item_category: `Vin ${product.color.value}`,
                                        item_variant: product.degree ?? '',
                                        item_list_name: 'Focus Sommelier',
                                    };
                                }),
                            },
                        });
                    }
                },
                Math.floor(Math.random() * (8000 - 5000 + 1)) + 2000,
            );

            // Define options for carousel
            this.options = {
                mode: 'snap',
                slides: {
                    perView: 1,
                    spacing: 16,
                },
                breakpoints: {
                    '(min-width: 550px)': {
                        slides: {
                            perView: 1,
                            spacing: 48,
                        },
                    },
                    '(min-width: 768px)': {
                        slides: {
                            perView: this.products.length,
                            spacing: 64,
                        },
                    },
                },
            };

            // Copy options
            this.opts = JSON.parse(JSON.stringify(this.options));

            // Add events
            this.opts.initial = this.currentProductIndex;

            // On slide change, update current index
            this.opts.slideChanged = (s) => {
                // If the current slide is opened
                if (document.querySelectorAll('.product__see-more-button')[this.currentProductIndex].classList.contains('opened')) {
                    // Toggle to close it
                    this.toggleProduct(null, document.querySelectorAll('.product__see-more-button')[this.currentProductIndex]);
                }

                // Update the current index
                this.currentProductIndex = s.track.details.rel;
            };

            // Init the carousel
            this.carousel = new KeenSlider(this.$refs.container, this.opts);
        },

        methods: {
            switchToResult() {
                gsap.to(this.$refs.loadingContainer, {
                    opacity: 0,
                    display: 'none',
                    duration: 0.4,
                    ease: 'power2.out',
                });

                gsap.set(this.$refs.resultContainer, {
                    height: 'auto',
                    duration: 0.4,
                    delay: 0.4,
                    onComplete: this.enter,
                });
            },

            enter() {
                this.tl = gsap.timeline();

                this.tl.fromTo(
                    this.$refs.heading,
                    {
                        y: 20,
                        opacity: 0,
                    },
                    {
                        y: 0,
                        opacity: 1,
                        duration: 0.4,
                        ease: 'power2.out',
                    },
                );

                this.tl.fromTo(
                    document.querySelectorAll('.product__content'),
                    {
                        y: 20,
                        opacity: 0,
                    },
                    {
                        y: 0,
                        stagger: 0.2,
                        opacity: 1,
                        duration: 0.4,
                        ease: 'power2.out',
                    },
                );
            },

            restart() {
                this.$emit('restart');
            },

            toggleProduct(event, targetButton, index) {
                // Get the button if it's an event trigger or a parameter element
                const button = targetButton ? targetButton : event.target;

                // Update the text of the button
                button.innerHTML = button.classList.contains('opened') ? button.getAttribute('data-off') : button.getAttribute('data-on');

                // Change the class of the button for the arrow
                button.classList.toggle('btn-text--arrow-down');
                button.classList.toggle('btn-text--arrow-up');

                const indexToChange = window.innerWidth < 768 ? this.currentProductIndex : index;

                // Update the height of the product container to reveal/hide content
                gsap.to(document.querySelectorAll('.product__container')[indexToChange], {
                    height: button.classList.contains('opened') ? document.querySelectorAll('.product__preview')[indexToChange].clientHeight : document.querySelectorAll('.product__full')[indexToChange].clientHeight + 24,
                    duration: 0.6,
                    ease: 'power2.out',
                });

                // Toggle the class to confirm the state
                button.classList.toggle('opened');
            },
        },
    };
</script>

<style lang="scss">
    .keen-slider:not([data-keen-slider-disabled]) {
        @apply overflow-visible;
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.5s ease;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }

    .results__product {
        &--1 {
            @screen md {
                min-width: 768px !important;
                max-width: 768px !important;
            }
        }

        &--2 {
            @screen md {
                min-width: 340px !important;
                max-width: 340px !important;
            }
        }
    }
</style>
